import { useState } from "react";
import type { Document, RenderEmailResponse } from "@markeroo/shared";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "@/components/Modal";
import { Dropdown, type DropdownItem } from "@/components/Dropdown";
import { mutedText } from "@/styles/variants";
import { buttonVariants } from "@markeroo/theme";
import { ui, notifications } from "@/strings";
import { papersForName, studentCanViewFeedbackUsingLink, studentWillSeeFeedbackForAllPapers, shareWithStudent } from "@/strings/labels";
import { useNotification } from "@/providers/NotificationProvider";
import PiEnvelopeDefaultStroke from "@markeroo/icons/src/stroke/communication/PiEnvelopeDefaultStroke";
import PiClipboardCheckStroke from "@markeroo/icons/src/stroke/files-&-folders/PiClipboardCheckStroke";
import PiAlertCircleStroke from "@markeroo/icons/src/stroke/alerts/PiAlertCircleStroke";

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  document: Document | null;
  studentName: string;
  studentEmail?: string | null;
  papers: Document[];
  isCreating: boolean;
  isRevoking: boolean;
  onCreateShare: (documentId: string) => void;
  onRevokeShare: (documentId: string) => void;
  onCopyShareLink: (documentId: string, studentId: string) => Promise<void>;
  onEditStudent?: () => void;
  renderedEmail?: RenderEmailResponse | null;
  isLoadingEmail?: boolean;
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function ShareModal({
  isOpen,
  onClose,
  document: doc,
  studentName,
  studentEmail,
  papers,
  isCreating,
  isRevoking,
  onCreateShare,
  onRevokeShare,
  onCopyShareLink,
  onEditStudent,
  renderedEmail,
  isLoadingEmail,
}: ShareModalProps) {
  const [showCopiedFeedback, setShowCopiedFeedback] = useState(false);
  const [showEmailCopiedFeedback, setShowEmailCopiedFeedback] = useState(false);
  const { show } = useNotification();

  // Early return if modal is closed or document is null
  // This prevents accessing doc properties before the document is available
  if (!isOpen || !doc) {
    return null;
  }

  const shareActive = Boolean(doc.shareToken);
  const hasEmail = Boolean(studentEmail);

  const handleCopyClick = async () => {
    if (!doc.shareToken) return;
    await onCopyShareLink(doc.id, doc.studentId);
    setShowCopiedFeedback(true);
    setTimeout(() => {
      setShowCopiedFeedback(false);
    }, 2000);
  };

  const handleCreateShare = () => {
    onCreateShare(doc.id);
  };

  const handleRevokeShare = () => {
    onRevokeShare(doc.id);
  };

  const handleCopyEmail = async () => {
    if (!renderedEmail) return;
    try {
      // Copy both subject and body as formatted text
      const emailText = `Subject: ${renderedEmail.subject}\n\n${renderedEmail.body}`;
      await navigator.clipboard.writeText(emailText);
      setShowEmailCopiedFeedback(true);
      show(notifications.emailTemplates.emailCopied, "success");
      setTimeout(() => {
        setShowEmailCopiedFeedback(false);
      }, 2000);
    } catch {
      show(notifications.emailTemplates.copyFailed, "error");
    }
  };

  const handleSendEmail = () => {
    if (!renderedEmail || !studentEmail) return;
    // Open mailto link with subject and body
    const subject = encodeURIComponent(renderedEmail.subject);
    const body = encodeURIComponent(renderedEmail.body);
    const mailtoLink = `mailto:${studentEmail}?subject=${subject}&body=${body}`;
    window.open(mailtoLink, "_blank");
  };

  // Sort papers by createdAt descending (newest first)
  const sortedPapers = [...papers].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalHeader
        title={shareActive ? ui.actions.manageShare : `${ui.actions.share} ${papersForName(studentName)}?`}
        onClose={onClose}
      />
      <ModalBody>
        {shareActive ? (
          <>
            <p className="mb-4">{ui.labels.shareLinkActiveFor}</p>
            <ul className="list-disc ml-6 space-y-2">
              {sortedPapers.map((paper) => (
                <li key={paper.id}>
                  <span className="font-medium capitalize">{paper.status === 'final' ? ui.labels.final : ui.labels.draft}</span>
                  {" • "}
                  <span className="text-theme-secondary">{formatDate(paper.createdAt)}</span>
                </li>
              ))}
            </ul>
            <p className={`mt-4 ${mutedText}`}>{studentCanViewFeedbackUsingLink(studentName)}</p>

            {/* Email section when share is active */}
            {!hasEmail ? (
              <div className="mt-4 p-4 rounded-lg bg-semantic-warning-bg border border-semantic-warning">
                <div className="flex items-start gap-3">
                  <PiAlertCircleStroke size={20} className="text-semantic-warning flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-semantic-warning-text">{ui.labels.studentHasNoEmail}</p>
                    <p className={`${mutedText} mt-1`}>{ui.labels.addStudentEmailFirst}</p>
                    {onEditStudent && (
                      <button
                        type="button"
                        onClick={onEditStudent}
                        className="mt-2 text-sm font-medium text-semantic-warning-text hover:text-semantic-warning-text underline"
                      >
                        {ui.actions.edit} {ui.labels.student}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ) : null}
          </>
        ) : (
          <>
            <p className="mb-4">{ui.labels.shareCreatesViewOnly}</p>
            <ul className="list-disc ml-6 space-y-2">
              {sortedPapers.map((paper) => (
                <li key={paper.id}>
                  <span className="font-medium capitalize">{paper.status === 'final' ? ui.labels.final : ui.labels.draft}</span>
                  {" • "}
                  <span className="text-theme-secondary">{formatDate(paper.createdAt)}</span>
                </li>
              ))}
            </ul>
            <p className={`mt-4 ${mutedText}`}>{studentWillSeeFeedbackForAllPapers(studentName)}</p>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        {shareActive ? (
          <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center w-full gap-3">
            <button
              type="button"
              className="text-semantic-error hover:text-semantic-error-hover font-medium text-sm disabled:opacity-50 sm:ml-1 order-last sm:order-first"
              onClick={handleRevokeShare}
              disabled={isRevoking}
            >
              {isRevoking ? ui.actions.unsharing : ui.actions.unshare}
            </button>
            <div className="flex flex-col sm:flex-row gap-2">
              {hasEmail && (
                <Dropdown
                  label={ui.actions.sendEmail}
                  disabled={isLoadingEmail || !renderedEmail}
                  align="right"
                  direction="up"
                  items={[
                    {
                      key: "copy",
                      label: showEmailCopiedFeedback ? ui.labels.copied : ui.actions.copyEmail,
                      icon: <PiClipboardCheckStroke size={16} />,
                      onClick: () => { void handleCopyEmail(); },
                    },
                    {
                      key: "send",
                      label: ui.actions.openInEmailClient,
                      icon: <PiEnvelopeDefaultStroke size={16} />,
                      onClick: handleSendEmail,
                    },
                  ] satisfies DropdownItem[]}
                />
              )}
              <button
                type="button"
                className={buttonVariants.primary}
                onClick={() => {
                  void handleCopyClick();
                }}
                disabled={isCreating || isRevoking}
              >
                {showCopiedFeedback ? ui.labels.copied : ui.actions.copyShareLink}
              </button>
            </div>
          </div>
        ) : (
          <>
            <button
              type="button"
              className={buttonVariants.secondary}
              onClick={onClose}
              disabled={isCreating}
            >
              {ui.actions.cancel}
            </button>
            <button
              type="button"
              className={buttonVariants.primary}
              onClick={handleCreateShare}
              disabled={isCreating}
            >
              {isCreating ? ui.actions.creating : shareWithStudent(studentName.split(" ")[0] || studentName)}
            </button>
          </>
        )}
      </ModalFooter>
    </Modal>
  );
}
